<template>
  <div
    class="relative"
    @keydown="onKeydown"
    tabindex="0"
    ref="listContainer"
  >
    <!-- Stale data banner -->
    <div
      v-if="fetchError"
      class="flex items-center gap-2 px-4 py-2 text-xs text-amber-800 bg-amber-50 border-b border-amber-200"
    >
      <FontAwesomeIcon :icon="['fas', 'exclamation-triangle']" />
      <span>Couldn't refresh — showing the last data we had.</span>
      <button class="font-semibold text-amber-900 hover:underline ml-1" @click="retryNow">Retry</button>
    </div>
    <!-- Toolbar -->
    <div class="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-gray-200">
      <!-- Search -->
      <div class="relative">
        <FontAwesomeIcon
          :icon="['fas', 'search']"
          class="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-300 text-xs"
        />
        <input
          v-model="searchQuery"
          class="text-sm border border-gray-300 rounded pl-8 pr-8 py-1.5 w-44"
          placeholder="Search or #id..."
        />
        <button
          v-if="searchQuery"
          class="absolute right-2.5 inset-y-0 flex items-center text-gray-400 hover:text-gray-600"
          @click="searchQuery = ''"
        >
          <FontAwesomeIcon :icon="['fas', 'xmark']" class="text-xs" />
        </button>
      </div>
      <!-- Type chips: All / Feature Requests / Bugs & Support -->
      <div class="flex items-center gap-1" data-testid="type-chips">
        <button
          type="button"
          class="px-2.5 py-1 text-xs rounded transition-colors"
          :class="!typeChips.length ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'"
          @click="clearTypeChips"
        >All</button>
        <button
          v-for="chip in typeChipGroups"
          :key="chip.key"
          type="button"
          class="px-2.5 py-1 text-xs rounded transition-colors"
          :class="isTypeChipActive(chip) ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'"
          @click="toggleTypeChip(chip)"
        >{{ chip.label }}</button>
      </div>
      <!-- Quick filters -->
      <div class="flex rounded border border-gray-300 overflow-hidden">
        <button
          class="text-xs px-2 py-1.5 transition-colors"
          :class="!quickFilter ? 'bg-primary-50 text-primary-600' : 'text-gray-500 hover:text-gray-700'"
          @click="setQuickFilter(null)"
        >All</button>
        <button
          class="text-xs px-2 py-1.5 border-l border-gray-300 transition-colors"
          :class="quickFilter === 'my_open' ? 'bg-primary-50 text-primary-600' : 'text-gray-500 hover:text-gray-700'"
          title="Open items assigned to you"
          @click="setQuickFilter('my_open')"
        >Assigned to me</button>
        <button
          class="text-xs px-2 py-1.5 border-l border-gray-300 transition-colors"
          :class="quickFilter === 'unassigned' ? 'bg-primary-50 text-primary-600' : 'text-gray-500 hover:text-gray-700'"
          @click="setQuickFilter('unassigned')"
        >Unassigned</button>
        <button
          class="text-xs px-2 py-1.5 border-l border-gray-300 transition-colors"
          :class="quickFilter === 'overdue' ? 'bg-primary-50 text-primary-600' : 'text-gray-500 hover:text-gray-700'"
          @click="setQuickFilter('overdue')"
        >Overdue</button>
      </div>
      <!-- Filter button -->
      <div class="relative" ref="filterButtonContainer">
        <button
          @click.stop="toggleFilterPopover"
          ref="filterButton"
          class="text-xs border rounded px-2 py-1.5 inline-flex items-center gap-1.5"
          :class="activeFilterCount ? 'text-primary-600 border-primary-300 bg-primary-50' : 'text-gray-500 border-gray-300 hover:text-gray-700'"
        >
          <FontAwesomeIcon :icon="['fas', 'filter']" class="text-[10px]" />
          Filter
          <span v-if="activeFilterCount" class="bg-primary-500 text-white rounded-full text-[10px] px-1.5 py-0.5 leading-none">
            {{ activeFilterCount }}
          </span>
        </button>
        <Support_FilterPopover
          :visible="filterPopoverOpen"
          v-model="activeFilters"
          v-model:hide-completed="hideCompleted"
          :requirement-types="requirementTypes"
          :users="users"
          :tenant-filter-options="tenantFilterOptions"
          :all-tags="allTags"
          :source-filter-options="sourceFilterOptions"
          :anchor-rect="filterButtonRect"
          @close="filterPopoverOpen = false"
        />
      </div>
      <!-- View toggle -->
      <div class="flex rounded border border-gray-300 overflow-hidden">
        <button
          class="text-xs px-2 py-1.5 transition-colors"
          :class="viewMode === 'table' ? 'bg-primary-50 text-primary-600' : 'text-gray-500 hover:text-gray-700'"
          @click="viewMode = 'table'"
        >
          Table
        </button>
        <button
          class="text-xs px-2 py-1.5 border-l border-gray-300 transition-colors"
          :class="viewMode === 'tree' ? 'bg-primary-50 text-primary-600' : 'text-gray-500 hover:text-gray-700'"
          @click="viewMode = 'tree'"
        >
          Tree
        </button>
      </div>
      <div class="flex-1" />
      <span
        v-if="refreshing"
        class="text-xs text-gray-400 flex items-center gap-1.5"
      >
        <FontAwesomeIcon :icon="['fas', 'spinner']" class="animate-spin" />
        Updating...
      </span>
      <div v-else-if="updatedLabel" class="flex items-center gap-2 text-xs text-gray-400">
        <span>{{ updatedLabel }}</span>
        <button class="hover:text-gray-600" title="Refresh" @click="retryNow">
          <FontAwesomeIcon :icon="['fas', 'rotate-right']" />
        </button>
      </div>
      <ExportMenu :disabled="!allFlat.length" :exports="[{ type: 'csv', action: exportCsv }]" />
      <button
        v-if="viewMode === 'tree'"
        class="text-xs text-gray-500 hover:text-gray-700 px-2 py-1.5 border border-gray-300 rounded"
        @click="toggleExpandAll"
      >
        {{ allExpanded ? 'Collapse' : 'Expand' }}
      </button>
      <button
        class="text-xs px-2 py-1.5 border rounded"
        :class="bulkMode
          ? 'text-primary-600 border-primary-300 bg-primary-50'
          : 'text-gray-500 border-gray-300 hover:text-gray-700'"
        @click="toggleBulkMode"
      >
        Select
      </button>
      <button
        class="bg-primary-500 text-white text-xs font-medium px-3 py-1.5 rounded hover:bg-primary-600 transition-colors"
        @click="showCreateModal = true"
      >
        + New
      </button>
    </div>

    <!-- Active filter chips -->
    <div v-if="chipRowVisible" class="flex flex-wrap items-center gap-1.5 px-4 py-2 border-b border-gray-100 bg-gray-50">
      <Tag
        v-if="searchQuery.trim()"
        :label="`Search: ${searchQuery.trim()}`"
        color="default"
        :removable="true"
        @remove="searchQuery = ''"
      />
      <Tag
        v-if="quickFilter"
        :label="quickFilterLabel"
        color="default"
        :removable="true"
        @remove="quickFilter = null"
      />
      <Tag
        v-for="chip in typeChipGroups.filter(isTypeChipActive)"
        :key="'t' + chip.key"
        :label="`Type: ${chip.label}`"
        color="default"
        :removable="true"
        @remove="toggleTypeChip(chip)"
      />
      <Tag
        v-for="(chip, ci) in filterChips"
        :key="'c' + ci"
        :label="chipLabel(chip)"
        :color="isContradictory(chip) ? 'red' : 'default'"
        :removable="true"
        @remove="removeChipValue(chip.filterIdx, chip.value)"
      />
      <Tag
        v-if="hideCompleted"
        label="Hiding completed"
        color="default"
        :removable="true"
        @remove="hideCompleted = false"
      />
      <button @click="clearAllFilters" class="text-xs text-gray-400 hover:text-gray-600 ml-1">Clear all</button>
    </div>

    <!-- Bulk actions bar -->
    <div
      v-if="bulkMode && checkedIds.size > 0"
      class="flex items-center flex-wrap px-6 py-2 bg-primary-50 border-b border-primary-200 gap-3"
    >
      <span class="text-sm font-medium text-primary-700">{{ checkedIds.size }} selected</span>
      <select
        class="text-sm border border-primary-300 rounded px-2 py-1"
        @change="bulkUpdateStatus($event.target.value); $event.target.value = ''"
      >
        <option value="">Set Status...</option>
        <option v-for="s in bulkStatusOptions" :key="s.value" :value="s.value">{{ s.label }}</option>
      </select>
      <select
        class="text-sm border border-primary-300 rounded px-2 py-1"
        @change="bulkUpdatePriority($event.target.value); $event.target.value = ''"
      >
        <option value="">Set Priority...</option>
        <option v-for="code in priorityCodes" :key="code" :value="code">{{ priorityShortLabel(code) }}</option>
      </select>
      <select
        class="text-sm border border-primary-300 rounded px-2 py-1"
        @change="bulkUpdateOwner($event.target.value); $event.target.value = ''"
      >
        <option value="">Set Owner...</option>
        <option value="__none__">Unassigned</option>
        <option v-for="u in users" :key="u.id" :value="u.id">{{ u.full_name || u.email }}</option>
      </select>
      <div class="relative">
        <button
          class="text-sm border border-primary-300 rounded px-2 py-1 text-primary-700 hover:bg-primary-100"
          @click.stop="showBulkReparent = !showBulkReparent"
        >
          Move under...
        </button>
        <div
          v-if="showBulkReparent"
          class="absolute top-full left-0 mt-1 z-50 w-72 bg-white border border-gray-200 rounded-lg shadow-lg"
          @click.stop
        >
          <Support_SearchableSelect
            :model-value="null"
            :options="bulkReparentOptions"
            placeholder="Select new parent..."
            non-placeholder="None (top-level)"
            @update:model-value="bulkReparent($event); showBulkReparent = false"
          />
        </div>
      </div>
      <div class="relative">
        <button
          class="text-sm border border-primary-300 rounded px-2 py-1 text-primary-700 hover:bg-primary-100"
          @click.stop="bulkDropdown = bulkDropdown === 'add_tag' ? null : 'add_tag'; bulkTagInput = ''"
        >
          Add Tag...
        </button>
        <div
          v-if="bulkDropdown === 'add_tag'"
          class="absolute top-full left-0 mt-1 z-50 w-56 bg-white border border-gray-200 rounded-lg shadow-lg p-3"
          @click.stop
        >
          <input
            v-model="bulkTagInput"
            class="text-xs border border-gray-300 rounded px-2 py-1.5 w-full"
            placeholder="Type tag and press Enter..."
            @keydown.enter.prevent="bulkAddTag(bulkTagInput); bulkDropdown = null"
            @keydown.esc="bulkDropdown = null"
          />
        </div>
      </div>
      <div class="relative">
        <button
          class="text-sm border border-primary-300 rounded px-2 py-1 text-primary-700 hover:bg-primary-100"
          @click.stop="bulkDropdown = bulkDropdown === 'remove_tag' ? null : 'remove_tag'"
        >
          Remove Tag...
        </button>
        <div
          v-if="bulkDropdown === 'remove_tag'"
          class="absolute top-full left-0 mt-1 z-50 w-48 bg-white border border-gray-200 rounded-lg shadow-lg py-1"
          @click.stop
        >
          <p v-if="!bulkSelectedTags.length" class="text-xs text-gray-400 px-3 py-2">No tags on selected items</p>
          <button
            v-for="tag in bulkSelectedTags"
            :key="tag"
            class="w-full text-left text-xs px-3 py-1.5 hover:bg-gray-50 text-gray-700"
            @click="bulkRemoveTag(tag); bulkDropdown = null"
          >{{ tag }}</button>
        </div>
      </div>
      <div class="relative">
        <button
          class="text-sm border border-primary-300 rounded px-2 py-1 text-primary-700 hover:bg-primary-100"
          @click.stop="bulkDropdown = bulkDropdown === 'due_date' ? null : 'due_date'"
        >
          Due Date...
        </button>
        <div
          v-if="bulkDropdown === 'due_date'"
          class="absolute top-full left-0 mt-1 z-50 bg-white border border-gray-200 rounded shadow-lg p-3"
          @click.stop
        >
          <input
            type="date"
            class="text-xs border border-gray-300 rounded px-2 py-1.5 w-full"
            @change="bulkSetDueDate($event.target.value); bulkDropdown = null"
          />
          <button
            class="w-full text-left text-xs text-gray-400 hover:text-gray-600 mt-2"
            @click="bulkSetDueDate(null); bulkDropdown = null"
          >
            Clear due date
          </button>
        </div>
      </div>
      <span v-if="bulkBusy" class="text-xs text-gray-500 flex items-center gap-1">
        <FontAwesomeIcon :icon="['fas', 'spinner']" class="animate-spin" />
        Saving {{ bulkBusy }}...
      </span>
      <button
        class="text-xs text-gray-500 hover:text-gray-700 ml-auto"
        @click="checkedIds = new Set()"
      >
        Clear
      </button>
    </div>

    <!-- List -->
    <div class="overflow-y-auto" style="max-height: calc(100vh - 160px)">
      <div v-if="loading" class="px-6 py-12 text-center">
        <FontAwesomeIcon :icon="['fas', 'spinner']" class="text-gray-300 text-lg animate-spin" />
      </div>
      <template v-else-if="viewMode === 'tree'">
        <div v-if="!filteredTree.length" class="px-6 py-12 text-center text-sm text-gray-400">
          {{ searchQuery ? 'No items match your search.' : 'No items found.' }}
        </div>
        <draggable
          :key="treeVersion"
          :list="filteredTree"
          item-key="id"
          :group="{ name: 'requirements' }"
          handle=".drag-handle"
          ghost-class="drag-ghost"
          chosen-class="drag-chosen"
          data-parent-id=""
          @start="onRootDragStart"
          @end="onRootDragEnd"
        >
          <template #item="{ element }">
            <Support_Row
              :node="element"
              :depth="0"
              :selected-id="selectedIdNumber"
              :expanded-ids="expandedIds"
              :bulk-mode="bulkMode"
              :checked-ids="checkedIds"
              :keyboard-focused-id="keyboardFocusedId"
              :tenant-options="tenantOptions"
              :users="users"
              :comment-counts="commentCounts"
              :filter-match-ids="filterMatchIds"
              :has-active-filters="hasActiveFilters"
              @select="onSelect"
              @toggle="onToggle"
              @quick-add="onQuickAdd"
              @toggle-check="onToggleCheck"
              @update-field="onUpdateField"
              @update-title="onUpdateTitle"
              @reorder="onReorder"
              @reparent="onReparent"
              @actions-menu="onActionsMenu"
            />
          </template>
        </draggable>
        <Support_QuickAdd
          v-if="quickAddParent"
          :parent-node="quickAddParent"
          :depth="quickAddDepth"
          :requirement-types="requirementTypes"
          @created="onQuickAddCreated"
          @cancel="quickAddParent = null"
        />
      </template>
      <template v-else>
        <div v-if="!tableData.length" class="px-6 py-12 text-center text-sm text-gray-400">
          {{ searchQuery ? 'No items match your search.' : 'No items found.' }}
        </div>
        <Support_Table
          v-else
          :items="tableData"
          :all-items="allFlat"
          :selected-id="selectedIdNumber"
          :keyboard-focused-id="keyboardFocusedId"
          :comment-counts="commentCounts"
          :filter-match-ids="filterMatchIds"
          :has-active-filters="hasActiveFilters"
          :sort-field="sortField"
          :sort-dir="sortDir"
          :bulk-mode="bulkMode"
          :checked-ids="checkedIds"
          :tenant-options="tenantOptions"
          :users="users"
          :page-reset-key="filterSignature"
          @select="onSelect"
          @sort="onSortColumn"
          @update-field="onUpdateField"
          @toggle-check="onToggleCheck"
          @toggle-all="onToggleAll"
          @actions-menu="onActionsMenu"
        />
      </template>
    </div>

    <!-- Detail tray -->
    <Support_ItemDetail
      :item-id="selectedIdNumber"
      :users="users"
      :requirement-types="requirementTypes"
      :tenant-options="tenantOptions"
      :all-items="allFlat"
      scope="work"
      @close="closeDetail"
      @comment-count="onCommentCount"
      @select-item="onSelectItem"
      @deleted="onRequirementDeleted"
    />

    <!-- Create Modal -->
    <Support_CreateModal
      v-if="showCreateModal"
      :requirement-types="requirementTypes"
      :users="users"
      :tenant-options="tenantOptions"
      :parent-options="allFlat"
      :default-parent="createModalParentId"
      @close="showCreateModal = false; createModalParentId = null"
      @created="onRequirementCreated"
    />

    <!-- Context Menu -->
    <Support_ContextMenu
      :visible="contextMenu.visible"
      :x="contextMenu.x"
      :y="contextMenu.y"
      :item="contextMenu.item"
      @close="contextMenu.visible = false"
      @add-child="openCreateWithParent"
      @change-parent="onContextChangeParent"
      @copy-link="onContextCopyLink"
      @delete="onContextDelete"
      @delete-promote="onContextDeletePromote"
    />
  </div>
</template>

<script>
import axios from 'axios';
import debounce from 'lodash/debounce';
import { mapGetters } from 'vuex';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import draggable from 'vuedraggable';
import Support_ItemDetail from './Support_ItemDetail.vue';
import Support_CreateModal from './Support_CreateModal.vue';
import Support_Row from './Support_Row.vue';
import Support_QuickAdd from './Support_QuickAdd.vue';
import Support_Table from './Support_Table.vue';
import Support_FilterPopover from './Support_FilterPopover.vue';
import Support_ContextMenu from './Support_ContextMenu.vue';
import Support_SearchableSelect from './Support_SearchableSelect.vue';
import undoQueue from './supportUndo';
import {
  DONE_STATUSES,
  PRIORITY_CODES,
  SELECTABLE_STATUSES,
  SERVER_DONE_STATUSES,
  STATUS_LABELS,
  TYPE_CHIP_GROUPS,
  WORK_TYPES,
  calendarDayValue,
  canParent,
  compareWorkItems,
  daysUntil,
  defaultSortFor,
  getDescendantIds,
  matchesSearch,
  mergeSiblingOrder,
  priorityShortLabel,
} from './support_constants.js';
import { useToast } from 'vue-toastification';
import Tag from '@/components/widgetcomponents/library/Tag.vue';
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue';

const DATE_FILTER_FIELDS = new Set(['due_date', 'created_at', 'updated_at']);
const COUNT_FILTER_FIELDS = new Set(['comment_count', 'children_count']);
const SCALAR_OPERATORS = new Set(['on_or_before', 'on_or_after']);
const KAIRON_DEMO_TENANT_ID = 3;

// Drafts and the demo tenant are hidden by default through ordinary filter
// rows the user can remove, never by a hard exclusion.
function defaultActiveFilters() {
  return [
    { field: 'status', operator: 'none_of', value: ['draft'] },
    { field: 'tenant', operator: 'none_of', value: [KAIRON_DEMO_TENANT_ID] },
  ];
}

function countValue(row, field, commentCounts) {
  if (field === 'comment_count') {
    return commentCounts?.[row.id] ?? row.comment_count ?? 0;
  }
  return row[field] ?? 0;
}

function matchesDateBounds(row, bounds) {
  for (const { field, after, before } of bounds) {
    const day = calendarDayValue(row[field]);
    if (day === null) return false;
    if (after !== null && day < after) return false;
    if (before !== null && day > before) return false;
  }
  return true;
}

function matchesCountBounds(row, bounds, commentCounts) {
  for (const { field, min, max } of bounds) {
    const n = countValue(row, field, commentCounts);
    if (Number.isFinite(min) && n < min) return false;
    if (Number.isFinite(max) && n > max) return false;
  }
  return true;
}

function isScalarFilter(filter) {
  return SCALAR_OPERATORS.has(filter?.operator);
}

const SOURCE_LABELS = {
  email: 'Email',
  web: 'Web',
  internal_note: 'Internal Note',
  cerberus: 'Cerberus',
};

const FILTER_STATE_KEY = 'support_list_filters_v2';
const VIEW_MODES = ['table', 'tree'];
const QUICK_FILTER_LABELS = { my_open: 'Assigned to me', unassigned: 'Unassigned', overdue: 'Overdue' };
const SCOPE = 'work';

export default {
  name: 'Support_List',
  components: {
    FontAwesomeIcon,
    draggable,
    Support_ItemDetail,
    Support_CreateModal,
    Support_Row,
    Support_QuickAdd,
    Support_Table,
    Support_FilterPopover,
    Support_ContextMenu,
    Support_SearchableSelect,
    Tag,
    ExportMenu,
  },
  props: {
    selectedId: { type: [String, Number], default: null },
    entityId: { type: [String, Number], default: null },
  },
  emits: ['select'],
  data() {
    return {
      viewMode: 'table',
      requirementTypes: [],
      users: [],
      searchQuery: '',
      sortByMode: {
        table: { ...defaultSortFor('table') },
        tree: { ...defaultSortFor('tree') },
      },
      showCreateModal: false,
      createModalParentId: null,
      contextMenu: { visible: false, x: 0, y: 0, item: null },
      showBulkReparent: false,
      bulkDropdown: null,
      bulkTagInput: '',
      bulkBusy: 0,
      expandedIds: new Set(),
      expandedBeforeSearch: null,
      bulkMode: false,
      checkedIds: new Set(),
      keyboardFocusedId: null,
      quickAddParent: null,
      quickFilter: null,
      typeChips: [],
      activeFilters: defaultActiveFilters(),
      hideCompleted: true,
      filterPopoverOpen: false,
      filterButtonRect: null,
      commentCounts: {},
      treeVersion: 0,
      now: Date.now(),
      priorityCodes: PRIORITY_CODES.filter((c) => c !== 'none'),
    };
  },
  computed: {
    ...mapGetters('auth', ['current_user']),
    ...mapGetters('tenant', ['current_tenant_slug']),
    toast() {
      return useToast();
    },
    allFlat() {
      return this.$store.getters['supportItems/rows'](SCOPE);
    },
    collectionState() {
      return this.$store.getters['supportItems/collection'](SCOPE);
    },
    loading() {
      return this.collectionState.loading && !this.allFlat.length;
    },
    refreshing() {
      return this.collectionState.refreshing;
    },
    fetchError() {
      return this.collectionState.fetchError;
    },
    lastFetchedAt() {
      return this.collectionState.lastFetchedAt;
    },
    selectedIdNumber() {
      return this.selectedId != null && this.selectedId !== '' ? Number(this.selectedId) : null;
    },
    updatedLabel() {
      if (!this.lastFetchedAt) return '';
      const diffSec = Math.max(0, Math.round((this.now - this.lastFetchedAt) / 1000));
      if (diffSec < 10) return 'Updated just now';
      if (diffSec < 60) return `Updated ${diffSec}s ago`;
      const diffMin = Math.round(diffSec / 60);
      if (diffMin < 60) return `Updated ${diffMin}m ago`;
      const diffHr = Math.round(diffMin / 60);
      return `Updated ${diffHr}h ago`;
    },
    // Terminal rows are only fetched when a view actually needs them.
    wantsDoneRows() {
      if (!this.hideCompleted) return true;
      return this.activeFilters.some(
        (f) => f.field === 'status' && f.operator === 'any_of' && (f.value || []).some((s) => SERVER_DONE_STATUSES.has(s)),
      );
    },
    fetchParams() {
      const params = {};
      if (!this.wantsDoneRows) params.open_only = true;
      if (this.entityId) {
        params.entity = this.entityId;
        params.include_descendants = true;
      }
      return params;
    },
    fetchParamsKey() {
      return JSON.stringify(this.fetchParams);
    },
    tenantOptions() {
      const seen = new Map();
      for (const r of this.allFlat) {
        if (r.tenant && !seen.has(r.tenant)) {
          seen.set(r.tenant, { id: r.tenant, name: r.tenant_name || `Tenant ${r.tenant}` });
        }
      }
      return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
    },
    tenantFilterOptions() {
      const options = this.tenantOptions.map(t => ({ value: t.id, label: t.name }));
      if (!options.some((o) => o.value === KAIRON_DEMO_TENANT_ID)) {
        options.push({ value: KAIRON_DEMO_TENANT_ID, label: 'Kairon Demo' });
      }
      const hasNull = this.allFlat.some(r => !r.tenant);
      if (hasNull) {
        options.unshift({ value: '__none__', label: 'No Tenant' });
      }
      return options;
    },
    sourceFilterOptions() {
      const seen = new Set(this.allFlat.map(r => r.source || '__none__'));
      const options = Object.keys(SOURCE_LABELS)
        .filter(key => seen.has(key))
        .map(key => ({ value: key, label: SOURCE_LABELS[key] }));
      if (seen.has('__none__')) options.unshift({ value: '__none__', label: 'No source' });
      return options;
    },
    typeChipGroups() {
      return TYPE_CHIP_GROUPS;
    },
    quickFilterLabel() {
      return QUICK_FILTER_LABELS[this.quickFilter] || this.quickFilter;
    },
    bulkStatusOptions() {
      return SELECTABLE_STATUSES.map((value) => ({ value, label: STATUS_LABELS[value] || value }));
    },
    parentTitles() {
      const map = {};
      for (const item of this.allFlat) map[item.id] = item.title;
      return map;
    },
    allowedTypeNames() {
      return this.typeChips.length ? new Set(this.typeChips) : WORK_TYPES;
    },
    staffUserIds() {
      return new Set(this.users.map(u => u.id));
    },
    filterChips() {
      const chips = [];
      for (const [idx, filter] of this.activeFilters.entries()) {
        if (isScalarFilter(filter)) {
          chips.push({ filterIdx: idx, field: filter.field, operator: filter.operator, value: filter.value, scalar: true });
          continue;
        }
        if (!Array.isArray(filter.value) || !filter.value.length) {
          chips.push({ filterIdx: idx, field: filter.field, operator: filter.operator, value: null });
          continue;
        }
        for (const val of filter.value) {
          chips.push({ filterIdx: idx, field: filter.field, operator: filter.operator, value: val });
        }
      }
      return chips;
    },
    chipRowVisible() {
      return this.filterChips.length || this.hideCompleted || this.quickFilter || this.typeChips.length || this.searchQuery.trim();
    },
    selectedTenantIds() {
      const f = this.activeFilters.find(f => f.field === 'tenant' && f.operator === 'any_of');
      return f?.value?.length ? f.value : null;
    },
    selectedTags() {
      const f = this.activeFilters.find(f => f.field === 'tags' && f.operator === 'any_of');
      return f?.value?.length ? f.value : [];
    },
    activeFilterCount() {
      const valCount = this.activeFilters.reduce(
        (n, f) => n + (isScalarFilter(f) ? 1 : f.value?.length || 0),
        0,
      );
      return valCount + (this.hideCompleted ? 1 : 0);
    },
    activeSortSlot() {
      return this.sortByMode[this.viewMode] || this.sortByMode.table;
    },
    sortField: {
      get() { return this.activeSortSlot.field; },
      set(field) { this.activeSortSlot.field = field; },
    },
    sortDir: {
      get() { return this.activeSortSlot.dir; },
      set(dir) { this.activeSortSlot.dir = dir; },
    },
    activeDefaultSort() {
      return defaultSortFor(this.viewMode);
    },
    listFilterState() {
      return {
        searchQuery: this.searchQuery,
        sortByMode: this.sortByMode,
        viewMode: this.viewMode,
        quickFilter: this.quickFilter,
        typeChips: this.typeChips,
        activeFilters: this.activeFilters,
        hideCompleted: this.hideCompleted,
      };
    },
    filterSignature() {
      return JSON.stringify(this.listFilterState);
    },
    _tabFilterResult() {
      const allowed = this.allowedTypeNames;
      const getVals = (field, op) => {
        const f = this.activeFilters.find(f => f.field === field && f.operator === op);
        return f?.value?.length ? f.value : null;
      };
      const getScalar = (field, op) => {
        const f = this.activeFilters.find(f => f.field === field && f.operator === op);
        if (!f || Array.isArray(f.value) || f.value === '' || f.value == null) return null;
        return f.value;
      };
      const dateBounds = [...DATE_FILTER_FIELDS].map((field) => ({
        field,
        after: calendarDayValue(getScalar(field, 'on_or_after')),
        before: calendarDayValue(getScalar(field, 'on_or_before')),
      })).filter(b => b.after !== null || b.before !== null);
      const countBounds = [...COUNT_FILTER_FIELDS].map((field) => {
        const min = getScalar(field, 'on_or_after');
        const max = getScalar(field, 'on_or_before');
        return {
          field,
          min: min == null ? null : Number(min),
          max: max == null ? null : Number(max),
        };
      }).filter(b => Number.isFinite(b.min) || Number.isFinite(b.max));
      const commentCounts = countBounds.some(b => b.field === 'comment_count')
        ? this.commentCounts
        : null;
      const typeAny = getVals('type', 'any_of');
      const typeNone = getVals('type', 'none_of');
      const statusAny = getVals('status', 'any_of');
      const statusNone = getVals('status', 'none_of');
      const ownerAny = getVals('owner', 'any_of');
      const ownerNone = getVals('owner', 'none_of');
      const requesterAny = getVals('requester', 'any_of');
      const requesterNone = getVals('requester', 'none_of');
      const priorityAny = getVals('priority', 'any_of');
      const priorityNone = getVals('priority', 'none_of');
      const tenantNone = getVals('tenant', 'none_of');
      const tagsNone = getVals('tags', 'none_of');
      const cfAny = getVals('customer_facing', 'any_of');
      const cfNone = getVals('customer_facing', 'none_of');
      const sourceAny = getVals('source', 'any_of');
      const sourceNone = getVals('source', 'none_of');
      const triageAny = getVals('needs_triage', 'any_of');
      const triageNone = getVals('needs_triage', 'none_of');
      const staffAny = getVals('owner_is_staff', 'any_of');
      const staffNone = getVals('owner_is_staff', 'none_of');
      const staffIds = this.staffUserIds;
      const currentUserId = this.current_user?.id;
      const ancestorsWanted = this.viewMode === 'tree';
      const trueMatchIds = new Set();
      const includeIds = new Set();
      const allById = Object.fromEntries(this.allFlat.map(r => [r.id, r]));
      for (const r of this.allFlat) {
        if (!allowed.has(r.requirement_type_name)) continue;
        if (this.quickFilter === 'my_open' && (DONE_STATUSES.has(r.status) || r.owner !== currentUserId)) continue;
        if (this.quickFilter === 'unassigned' && r.owner) continue;
        if (this.quickFilter === 'overdue' && !(daysUntil(r.due_date) < 0)) continue;
        if (this.hideCompleted && DONE_STATUSES.has(r.status)) continue;
        if (typeAny && !typeAny.includes(r.requirement_type)) continue;
        if (typeNone && typeNone.includes(r.requirement_type)) continue;
        if (statusAny && !statusAny.includes(r.status)) continue;
        if (statusNone && statusNone.includes(r.status)) continue;
        if (ownerAny) {
          if (!r.owner && !ownerAny.includes('__none__')) continue;
          if (r.owner && !ownerAny.includes(r.owner)) continue;
        }
        if (ownerNone) {
          if (!r.owner && ownerNone.includes('__none__')) continue;
          if (r.owner && ownerNone.includes(r.owner)) continue;
        }
        if (requesterAny) {
          if (!r.requester && !requesterAny.includes('__none__')) continue;
          if (r.requester && !requesterAny.includes(r.requester)) continue;
        }
        if (requesterNone) {
          if (!r.requester && requesterNone.includes('__none__')) continue;
          if (r.requester && requesterNone.includes(r.requester)) continue;
        }
        if (priorityAny && !priorityAny.includes(r.priority)) continue;
        if (priorityNone && priorityNone.includes(r.priority)) continue;
        if (!this.entityId && this.selectedTenantIds) {
          if (!r.tenant && !this.selectedTenantIds.includes('__none__')) continue;
          if (r.tenant && !this.selectedTenantIds.includes(r.tenant)) continue;
        }
        if (tenantNone) {
          if (!r.tenant && tenantNone.includes('__none__')) continue;
          if (r.tenant && tenantNone.includes(r.tenant)) continue;
        }
        if (tagsNone && r.tags?.some(t => tagsNone.includes(t))) continue;
        if (cfAny) {
          const val = r.customer_facing ? 'yes' : 'no';
          if (!cfAny.includes(val)) continue;
        }
        if (cfNone) {
          const val = r.customer_facing ? 'yes' : 'no';
          if (cfNone.includes(val)) continue;
        }
        if (sourceAny && !sourceAny.includes(r.source || '__none__')) continue;
        if (sourceNone && sourceNone.includes(r.source || '__none__')) continue;
        if (triageAny && !triageAny.includes(r.needs_triage ? 'yes' : 'no')) continue;
        if (triageNone && triageNone.includes(r.needs_triage ? 'yes' : 'no')) continue;
        if (staffAny || staffNone) {
          const val = r.owner && staffIds.has(r.owner) ? 'yes' : 'no';
          if (staffAny && !staffAny.includes(val)) continue;
          if (staffNone && staffNone.includes(val)) continue;
        }
        if (!matchesDateBounds(r, dateBounds)) continue;
        if (!matchesCountBounds(r, countBounds, commentCounts)) continue;
        trueMatchIds.add(r.id);
        includeIds.add(r.id);
        if (!ancestorsWanted) continue;
        let cur = r;
        while (cur.parent && allById[cur.parent]) {
          const parent = allById[cur.parent];
          if (!allowed.has(parent.requirement_type_name)) break;
          includeIds.add(parent.id);
          cur = parent;
        }
      }
      return { items: this.allFlat.filter(r => includeIds.has(r.id)), trueMatchIds };
    },
    tabFiltered() {
      return this._tabFilterResult.items;
    },
    filterMatchIds() {
      return this._tabFilterResult.trueMatchIds;
    },
    hasActiveFilters() {
      return this.activeFilters.length > 0 || this.quickFilter || this.hideCompleted || this.typeChips.length > 0 || this.selectedTags.length > 0 || this.searchQuery.trim();
    },
    allTags() {
      return [...new Set(this.allFlat.flatMap(r => r.tags || []))].sort();
    },
    bulkSelectedTags() {
      const tagSet = new Set();
      for (const id of this.checkedIds) {
        const item = this.itemById(id);
        for (const tag of item?.tags || []) tagSet.add(tag);
      }
      return [...tagSet].sort();
    },
    tagFiltered() {
      if (!this.selectedTags.length) return this.tabFiltered;
      const byId = Object.fromEntries(this.tabFiltered.map(r => [r.id, r]));
      const keepIds = new Set();
      const ancestorsWanted = this.viewMode === 'tree';
      for (const r of this.tabFiltered) {
        if (r.tags?.some(t => this.selectedTags.includes(t))) {
          keepIds.add(r.id);
          if (!ancestorsWanted) continue;
          let cur = r;
          while (cur.parent && byId[cur.parent]) {
            keepIds.add(cur.parent);
            cur = byId[cur.parent];
          }
        }
      }
      return this.tabFiltered.filter(r => keepIds.has(r.id));
    },
    tableData() {
      if (!this.searchQuery.trim()) return this.sortItems([...this.tagFiltered]);
      const q = this.searchQuery.trim();
      const ranked = [];
      for (const r of this.tagFiltered) {
        const rank = matchesSearch(r, q);
        if (rank != null) ranked.push({ item: r, rank });
      }
      // Relevance order while searching; a column sort still wins when set.
      ranked.sort((a, b) => a.rank - b.rank || compareWorkItems(a.item, b.item, this.sortField, this.sortDir, this.parentTitles));
      return ranked.map((r) => r.item);
    },
    tree() {
      return this.buildTree(this.tagFiltered);
    },
    filteredTree() {
      // treeVersion is read so a rejected drop can force a rebuild.
      void this.treeVersion;
      if (!this.searchQuery.trim()) return this.tree;
      const q = this.searchQuery.toLowerCase();
      return this.filterTree(this.tree, q);
    },
    allExpanded() {
      const expandable = this.allFlat.filter(r => r.children_count > 0);
      return expandable.length > 0 && expandable.every(r => this.expandedIds.has(r.id));
    },
    flatVisibleIds() {
      if (this.viewMode === 'table') return this.tableData.map((r) => r.id);
      const ids = [];
      const walk = (nodes) => {
        for (const node of nodes) {
          ids.push(node.id);
          const open = this.expandedIds.has(node.id) || (this.hasActiveFilters && !this.filterMatchIds.has(node.id));
          if (open && node.children?.length) {
            walk(node.children);
          }
        }
      };
      walk(this.filteredTree);
      return ids;
    },
    bulkReparentOptions() {
      if (!this.checkedIds.size) return this.allFlat;
      const excluded = getDescendantIds([...this.checkedIds], this.allFlat);
      const selectedTypes = [...new Set(
        [...this.checkedIds]
          .map((id) => this.itemById(id)?.requirement_type_name)
          .filter(Boolean),
      )];
      return this.allFlat.filter((item) => {
        if (excluded.has(item.id)) return false;
        return selectedTypes.every((childType) => canParent(item.requirement_type_name, childType));
      });
    },
    quickAddDepth() {
      if (!this.quickAddParent) return 0;
      let depth = 1;
      let current = this.quickAddParent;
      const byId = {};
      for (const item of this.allFlat) byId[item.id] = item;
      while (current.parent && byId[current.parent]) {
        depth++;
        current = byId[current.parent];
      }
      return depth;
    },
  },
  watch: {
    activeFilters: {
      deep: true,
      handler() {
        if (this._syncGuard === 0) this.syncFiltersToUrl();
      },
    },
    fetchParamsKey() {
      if (this._syncGuard === 0) this.loadRequirements();
    },
    searchQuery(q, previous) {
      if (this._syncGuard === 0) {
        const wasSearching = !!previous?.trim();
        const isSearching = !!q.trim();
        if (isSearching && !wasSearching) this.expandedBeforeSearch = new Set(this.expandedIds);
        if (isSearching) {
          const query = q.trim();
          const expand = new Set();
          const walk = (nodes, ancestors) => {
            for (const node of nodes) {
              if (matchesSearch(node, query) != null) {
                for (const id of ancestors) expand.add(id);
              }
              if (node.children?.length) {
                walk(node.children, [...ancestors, node.id]);
              }
            }
          };
          walk(this.tree, []);
          if (expand.size) {
            const merged = new Set(this.expandedIds);
            for (const id of expand) merged.add(id);
            this.expandedIds = merged;
          }
        } else if (wasSearching && this.expandedBeforeSearch) {
          this.expandedIds = this.expandedBeforeSearch;
          this.expandedBeforeSearch = null;
        }
      }
      this._debouncedSyncUrl();
    },
    filterPopoverOpen(open) {
      if (open) {
        this.updateFilterButtonRect();
        window.addEventListener('scroll', this.updateFilterButtonRect, true);
        window.addEventListener('resize', this.updateFilterButtonRect);
      } else {
        window.removeEventListener('scroll', this.updateFilterButtonRect, true);
        window.removeEventListener('resize', this.updateFilterButtonRect);
      }
    },
    hideCompleted() {
      this.syncFiltersToUrl();
    },
    typeChips() {
      this.syncFiltersToUrl();
    },
    quickFilter() {
      this.syncFiltersToUrl();
    },
    sortField() {
      this.syncFiltersToUrl();
    },
    sortDir() {
      this.syncFiltersToUrl();
    },
    '$route.query': {
      handler(newQuery, oldQuery) {
        if (this._syncGuard > 0) return;
        if (JSON.stringify(newQuery) === JSON.stringify(oldQuery)) return;
        this.parseQueryToFilters();
      },
    },
    current_tenant_slug() {
      this.reloadForContext();
    },
    'current_user.id'() {
      this.reloadForContext();
    },
    listFilterState: {
      deep: true,
      handler(state) {
        if (this._syncGuard > 0) return;
        localStorage.setItem(FILTER_STATE_KEY, JSON.stringify(state));
      },
    },
  },
  created() {
    this._syncGuard = 0;
    this._urlSyncPending = false;
    this._debouncedSyncUrl = debounce(() => this.syncFiltersToUrl(), 300);
  },
  async mounted() {
    this._syncGuard++;
    const filterFields = ['owner', 'tenant', 'status', 'type', 'priority', 'tags', 'customer_facing', 'requester', 'source', 'needs_triage', 'owner_is_staff'];
    const scalarKeys = [...DATE_FILTER_FIELDS, ...COUNT_FILTER_FIELDS].flatMap(f => [`${f}_from`, `${f}_to`]);
    const filterKeys = new Set([
      ...filterFields,
      ...filterFields.map(f => `${f}_not`),
      ...scalarKeys,
      'search', 'hide_done', 'sort', 'dir', 'types', 'quick',
    ]);
    if (Object.keys(this.$route.query).some(k => filterKeys.has(k))) {
      this.parseQueryToFilters();
    } else {
      this.restoreFilters();
    }
    this.hydrateFromCache();
    this.$nextTick(() => { this._syncGuard--; });
    this.loadRequirementTypes();
    this.loadUsers();
    await this.loadRequirements();
    window.addEventListener('focus', this.onRevalidate);
    this._closeBulkDropdown = () => { this.bulkDropdown = null; this.showBulkReparent = false; };
    document.addEventListener('click', this._closeBulkDropdown);
    this.pollTimer = setInterval(this.onRevalidate, 60000);
    this.clockTimer = setInterval(() => { this.now = Date.now(); }, 15000);
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.updateFilterButtonRect, true);
    window.removeEventListener('resize', this.updateFilterButtonRect);
    window.removeEventListener('focus', this.onRevalidate);
    document.removeEventListener('click', this._closeBulkDropdown);
    clearInterval(this.pollTimer);
    clearInterval(this.clockTimer);
    this._debouncedSyncUrl?.cancel();
  },
  methods: {
    priorityShortLabel,
    itemById(id) {
      return this.$store.getters['supportItems/item'](id);
    },
    onRevalidate() {
      if (document.visibilityState === 'visible') this.loadRequirements();
    },
    hydrateFromCache() {
      this.$store.dispatch('supportItems/hydrateFromCache', {
        scope: SCOPE,
        tenantSlug: this.current_tenant_slug,
        userId: this.current_user?.id,
      });
    },
    async loadRequirements() {
      await this.$store.dispatch('supportItems/fetch', {
        scope: SCOPE,
        params: this.fetchParams,
        tenantSlug: this.current_tenant_slug,
        userId: this.current_user?.id,
      });
    },
    retryNow() {
      this.loadRequirements();
    },
    reloadForContext() {
      this.$store.dispatch('supportItems/clearScope', SCOPE);
      this.checkedIds = new Set();
      this.bulkMode = false;
      this.expandedIds = new Set();
      this.hydrateFromCache();
      this.loadRequirements();
    },
    restoreFilters() {
      try {
        const saved = localStorage.getItem(FILTER_STATE_KEY);
        if (!saved) return;
        const s = JSON.parse(saved);
        if (s.searchQuery) this.searchQuery = s.searchQuery;
        if (s.viewMode && VIEW_MODES.includes(s.viewMode)) this.viewMode = s.viewMode;
        for (const mode of ['table', 'tree']) {
          const savedSort = s.sortByMode?.[mode];
          if (savedSort?.field) this.sortByMode[mode].field = savedSort.field;
          if (savedSort?.dir) this.sortByMode[mode].dir = savedSort.dir;
        }
        if (s.quickFilter !== undefined) this.quickFilter = s.quickFilter;
        if (Array.isArray(s.typeChips)) this.typeChips = s.typeChips;
        if (Array.isArray(s.activeFilters)) this.activeFilters = s.activeFilters;
        if (s.hideCompleted != null) this.hideCompleted = s.hideCompleted;
      } catch {
        // ignore
      }
    },
    updateFilterButtonRect() {
      const btn = this.$refs.filterButton;
      if (btn) this.filterButtonRect = btn.getBoundingClientRect();
    },
    buildQuery() {
      const query = {};
      for (const f of this.activeFilters) {
        if (isScalarFilter(f)) {
          if (f.value === '' || f.value == null) continue;
          query[`${f.field}${f.operator === 'on_or_before' ? '_to' : '_from'}`] = String(f.value);
          continue;
        }
        if (!f.value?.length) continue;
        const key = f.operator === 'none_of' ? `${f.field}_not` : f.field;
        query[key] = f.value.join(',');
      }
      if (this.typeChips.length) query.types = this.typeChips.join(',');
      if (this.hideCompleted) query.hide_done = '1';
      if (this.quickFilter) query.quick = this.quickFilter;
      if (this.sortField !== this.activeDefaultSort.field) query.sort = this.sortField;
      if (this.sortDir !== this.activeDefaultSort.dir) query.dir = this.sortDir;
      if (this.searchQuery.trim()) query.search = this.searchQuery.trim();
      return query;
    },
    syncFiltersToUrl() {
      if (this._syncGuard > 0) {
        this._urlSyncPending = true;
        return;
      }
      this._urlSyncPending = false;
      const query = this.buildQuery();
      const current = this.$route.query;
      const keys = new Set([...Object.keys(query), ...Object.keys(current)]);
      let changed = false;
      for (const k of keys) {
        if (String(query[k] || '') !== String(current[k] || '')) {
          changed = true;
          break;
        }
      }
      if (!changed) return;

      this._syncGuard++;
      this.$router.replace({ query })
        .catch(() => {})
        .finally(() => {
          this._syncGuard--;
          if (this._urlSyncPending) this.syncFiltersToUrl();
        });
    },
    parseQueryToFilters() {
      const q = this.$route.query;
      this._syncGuard++;

      const toStr = (v) => Array.isArray(v) ? v.join(',') : v;
      const filters = [];
      const numericFields = ['owner', 'requester', 'type'];
      const stringFields = ['status', 'priority', 'tags', 'customer_facing', 'source', 'needs_triage', 'owner_is_staff'];

      const parseNum = v => {
        const n = Number(v);
        return Number.isNaN(n) ? null : n;
      };
      const parseStr = v => v || null;
      const parseSentinelOrNum = v => v === '__none__' ? v : parseNum(v);

      const addFilter = (field, operator, raw, parseVal) => {
        const values = toStr(raw).split(',').map(parseVal).filter(v => v !== null);
        if (values.length) filters.push({ field, operator, value: values });
      };

      for (const field of numericFields) {
        const parse = field === 'owner' || field === 'requester' ? parseSentinelOrNum : parseNum;
        if (q[field]) addFilter(field, 'any_of', q[field], parse);
        if (q[`${field}_not`]) addFilter(field, 'none_of', q[`${field}_not`], parse);
      }
      for (const field of stringFields) {
        if (q[field]) addFilter(field, 'any_of', q[field], parseStr);
        if (q[`${field}_not`]) addFilter(field, 'none_of', q[`${field}_not`], parseStr);
      }
      if (q.tenant) addFilter('tenant', 'any_of', q.tenant, parseSentinelOrNum);
      if (q.tenant_not) addFilter('tenant', 'none_of', q.tenant_not, parseSentinelOrNum);

      for (const field of DATE_FILTER_FIELDS) {
        if (q[`${field}_from`]) filters.push({ field, operator: 'on_or_after', value: toStr(q[`${field}_from`]) });
        if (q[`${field}_to`]) filters.push({ field, operator: 'on_or_before', value: toStr(q[`${field}_to`]) });
      }
      for (const field of COUNT_FILTER_FIELDS) {
        const from = q[`${field}_from`] ? parseNum(toStr(q[`${field}_from`])) : null;
        const to = q[`${field}_to`] ? parseNum(toStr(q[`${field}_to`])) : null;
        if (from !== null) filters.push({ field, operator: 'on_or_after', value: from });
        if (to !== null) filters.push({ field, operator: 'on_or_before', value: to });
      }

      this.typeChips = q.types ? toStr(q.types).split(',').filter(Boolean) : [];
      this.activeFilters = filters;
      this.hideCompleted = q.hide_done === '1';
      this.quickFilter = QUICK_FILTER_LABELS[toStr(q.quick)] ? toStr(q.quick) : null;
      this.sortField = toStr(q.sort) || this.activeDefaultSort.field;
      this.sortDir = toStr(q.dir) === 'desc' ? 'desc' : this.activeDefaultSort.dir;
      this.searchQuery = toStr(q.search) || '';

      this.$nextTick(() => { this._syncGuard--; });
    },
    toggleFilterPopover() {
      if (!this.filterPopoverOpen) {
        this.updateFilterButtonRect();
      }
      this.filterPopoverOpen = !this.filterPopoverOpen;
    },
    removeChipValue(filterIdx, value) {
      if (value === null || isScalarFilter(this.activeFilters[filterIdx])) {
        this.activeFilters = this.activeFilters.filter((_, i) => i !== filterIdx);
        return;
      }
      this.activeFilters = this.activeFilters.map((f, i) => {
        if (i !== filterIdx) return f;
        const newVal = f.value.filter(v => v !== value);
        return newVal.length ? { ...f, value: newVal } : null;
      }).filter(Boolean);
    },
    setQuickFilter(value) {
      this.quickFilter = value;
    },
    toggleTypeChip(group) {
      const next = new Set(this.typeChips);
      if (group.types.every(t => next.has(t))) {
        group.types.forEach(t => next.delete(t));
      } else {
        group.types.forEach(t => next.add(t));
      }
      this.typeChips = [...next];
    },
    isTypeChipActive(group) {
      if (!this.typeChips.length) return false;
      return group.types.every(t => this.typeChips.includes(t));
    },
    clearTypeChips() {
      this.typeChips = [];
    },
    clearAllFilters() {
      this.activeFilters = [];
      this.hideCompleted = false;
      this.typeChips = [];
      this.quickFilter = null;
      this.searchQuery = '';
    },
    isContradictory(chip) {
      if (chip.value === null || chip.scalar) return false;
      const counterOp = chip.operator === 'any_of' ? 'none_of' : 'any_of';
      const counter = this.activeFilters.find(f => f.field === chip.field && f.operator === counterOp);
      return counter?.value?.includes(chip.value) ?? false;
    },
    resolveValueLabel(field, value) {
      if (value === null || value === undefined) return '(any)';
      if (field === 'type') {
        const t = this.requirementTypes.find(t => t.id === value);
        return t ? t.name : String(value);
      }
      if (field === 'owner' || field === 'requester') {
        if (value === '__none__') return field === 'owner' ? 'No owner' : 'No requester';
        const u = this.users.find(u => u.id === value);
        return u ? u.full_name : String(value);
      }
      if (field === 'tenant') {
        return this.tenantFilterOptions.find(o => o.value === value)?.label || String(value);
      }
      if (field === 'tags') return String(value);
      if (field === 'status') return STATUS_LABELS[value] || value;
      if (field === 'priority') return priorityShortLabel(value);
      if (field === 'source') {
        return value === '__none__' ? 'No source' : SOURCE_LABELS[value] || String(value);
      }
      if (field === 'customer_facing' || field === 'needs_triage' || field === 'owner_is_staff') {
        return value === 'yes' ? 'Yes' : 'No';
      }
      return String(value);
    },
    chipLabel(chip) {
      const fieldLabels = {
        type: 'Type', status: 'Status', owner: 'Owner', requester: 'Requester',
        tenant: 'Tenant', tags: 'Tags', priority: 'Priority',
        customer_facing: 'Customer Facing', source: 'Source',
        needs_triage: 'Needs Triage', owner_is_staff: 'Staff-owned',
        due_date: 'Due', created_at: 'Created', updated_at: 'Updated',
        comment_count: 'Comments', children_count: 'Children',
      };
      const fieldLabel = fieldLabels[chip.field] || chip.field;
      if (chip.scalar) {
        const isCount = COUNT_FILTER_FIELDS.has(chip.field);
        const opLabel = chip.operator === 'on_or_before'
          ? (isCount ? 'at most' : 'on or before')
          : (isCount ? 'at least' : 'on or after');
        return `${fieldLabel} ${opLabel} ${chip.value}`;
      }
      const opLabel = chip.operator === 'none_of' ? 'not' : '';
      const valueLabel = this.resolveValueLabel(chip.field, chip.value);
      return [fieldLabel, opLabel, valueLabel].filter(Boolean).join(' ');
    },
    buildTree(items) {
      const sorted = this.sortItems([...items]);
      const byId = {};
      const roots = [];
      for (const item of sorted) {
        byId[item.id] = { ...item, children: [] };
      }
      for (const item of sorted) {
        const node = byId[item.id];
        if (item.parent && byId[item.parent]) {
          byId[item.parent].children.push(node);
        } else {
          roots.push(node);
        }
      }
      return roots;
    },
    sortItems(items) {
      const field = this.sortField;
      const dir = this.sortDir;
      const titles = this.parentTitles;
      return items.sort((a, b) => compareWorkItems(a, b, field, dir, titles));
    },
    onSortColumn(key) {
      if (this.sortField !== key) {
        this.sortField = key;
        this.sortDir = 'asc';
      } else if (this.sortDir === 'asc') {
        this.sortDir = 'desc';
      } else {
        this.sortField = this.activeDefaultSort.field;
        this.sortDir = this.activeDefaultSort.dir;
      }
    },
    filterTree(nodes, query) {
      const result = [];
      for (const node of nodes) {
        const rank = matchesSearch(node, query);
        const nodeMatch = rank != null;
        const titleOrIdMatch = rank != null && rank <= 1;
        const filteredChildren = node.children?.length
          ? this.filterTree(node.children, query)
          : [];
        if (nodeMatch || filteredChildren.length) {
          result.push({ ...node, children: titleOrIdMatch ? node.children : filteredChildren });
        }
      }
      return result;
    },
    async loadRequirementTypes() {
      try {
        const response = await axios.get('/api/requirement-types/');
        this.requirementTypes = Array.isArray(response.data) ? response.data : [];
      } catch {
        this.requirementTypes = [];
      }
    },
    async loadUsers() {
      try {
        const response = await axios.get('/api/user/', { params: { include_staff: 'true' } });
        const data = Array.isArray(response.data) ? response.data : [];
        this.users = data.filter(u => u.email?.endsWith('@kaironhealth.com'));
      } catch {
        this.users = [];
      }
    },
    onSelect(item) {
      this.keyboardFocusedId = item.id;
      this.$emit('select', item.id);
    },
    onSelectItem(id) {
      this.$emit('select', id);
    },
    closeDetail() {
      this.$emit('select', null);
    },
    onToggle(id) {
      const next = new Set(this.expandedIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      this.expandedIds = next;
    },
    toggleExpandAll() {
      if (this.allExpanded) {
        this.expandedIds = new Set();
      } else {
        const next = new Set(this.expandedIds);
        for (const item of this.allFlat) {
          if (item.children_count > 0) next.add(item.id);
        }
        this.expandedIds = next;
      }
    },
    toggleBulkMode() {
      this.bulkMode = !this.bulkMode;
      if (!this.bulkMode) this.checkedIds = new Set();
    },
    onToggleCheck(id) {
      const next = new Set(this.checkedIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      this.checkedIds = next;
    },
    onToggleAll(ids) {
      this.checkedIds = new Set(ids);
    },
    loadedSelection(ids) {
      return ids.filter((id) => !!this.itemById(id));
    },
    fieldLabel(fieldName) {
      const labels = { status: 'Status', priority: 'Priority', owner: 'Owner', tenant: 'Tenant', title: 'Title', due_date: 'Due date', parent: 'Parent', tags: 'Tags' };
      return labels[fieldName] || fieldName;
    },
    // Optimistic edit through the store with an undo window; the PATCH goes
    // out when the toast settles and its response merges back into the row.
    writeField(id, fields, { url, message } = {}) {
      const item = this.itemById(id);
      if (!item) return;
      const previous = {};
      for (const key of Object.keys(fields)) previous[key] = item[key];
      const local = { ...fields };
      if ('owner' in fields) {
        const user = this.users.find((u) => u.id === fields.owner);
        local.owner_name = user ? (user.full_name || user.email) : null;
      }
      if ('tenant' in fields) {
        local.tenant_name = this.tenantOptions.find((t) => t.id === fields.tenant)?.name || null;
        previous.tenant_name = item.tenant_name;
      }
      if ('owner' in fields) previous.owner_name = item.owner_name;
      this.$store.dispatch('supportItems/patchLocal', { id, fields: local });
      const label = message || `${this.fieldLabel(Object.keys(fields)[0])} updated`;
      undoQueue.show(
        label,
        () => this.$store.dispatch('supportItems/commit', { id, payload: fields, url, localKeys: Object.keys(local) }),
        () => this.$store.dispatch('supportItems/revert', { id, fields: previous }),
      );
    },
    onUpdateField(payload) {
      const { id, ...fields } = payload;
      if ('status' in fields) {
        this.writeField(id, { status: fields.status }, {
          url: `/api/requirements/${id}/set-status/`,
          message: `Status set to ${STATUS_LABELS[fields.status] || fields.status}`,
        });
        return;
      }
      if ('priority' in fields) {
        this.writeField(id, { priority: fields.priority }, { message: `Priority set to ${priorityShortLabel(fields.priority)}` });
        return;
      }
      this.writeField(id, fields);
    },
    onUpdateTitle(payload) {
      this.writeField(payload.id, { title: payload.title }, { message: 'Title updated' });
    },
    async _bulkApply(ids, buildPayload, { url } = {}) {
      const loaded = this.loadedSelection(ids);
      if (!loaded.length) {
        this.checkedIds = new Set();
        return;
      }
      const jobs = [];
      for (const id of loaded) {
        const item = this.itemById(id);
        const payload = buildPayload(item);
        if (!payload) continue;
        const previous = {};
        for (const key of Object.keys(payload)) previous[key] = item[key];
        if ('owner' in payload) {
          previous.owner_name = item.owner_name;
          const user = this.users.find((u) => u.id === payload.owner);
          this.$store.dispatch('supportItems/patchLocal', { id, fields: { ...payload, owner_name: user ? (user.full_name || user.email) : null } });
        } else {
          this.$store.dispatch('supportItems/patchLocal', { id, fields: payload });
        }
        jobs.push({ id, payload, previous });
      }
      this.bulkBusy = jobs.length;
      const results = await Promise.allSettled(jobs.map((job) =>
        this.$store.dispatch('supportItems/commit', { id: job.id, payload: job.payload, url: url ? url(job.id) : undefined })));
      this.bulkBusy = 0;
      const failed = [];
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          failed.push(jobs[index].id);
          this.$store.dispatch('supportItems/revert', { id: jobs[index].id, fields: jobs[index].previous });
        }
      });
      if (failed.length) {
        this.toast.error(`${failed.length} of ${jobs.length} updates failed`);
      }
      this.checkedIds = new Set();
    },
    async bulkUpdateStatus(status) {
      if (!status) return;
      await this._bulkApply([...this.checkedIds], () => ({ status }), { url: (id) => `/api/requirements/${id}/set-status/` });
    },
    async bulkUpdatePriority(priority) {
      if (!priority) return;
      await this._bulkApply([...this.checkedIds], () => ({ priority }));
    },
    async bulkUpdateOwner(value) {
      if (!value) return;
      const owner = value === '__none__' ? null : Number(value);
      await this._bulkApply([...this.checkedIds], () => ({ owner }));
    },
    async bulkReparent(newParentId) {
      await this._bulkApply([...this.checkedIds], () => ({ parent: newParentId }));
    },
    async bulkAddTag(tag) {
      const trimmed = tag.trim();
      if (!trimmed) return;
      await this._bulkApply([...this.checkedIds], (item) => {
        const current = item?.tags || [];
        if (current.includes(trimmed)) return null;
        return { tags: [...current, trimmed] };
      });
    },
    async bulkRemoveTag(tag) {
      await this._bulkApply([...this.checkedIds], (item) => {
        const current = item?.tags || [];
        if (!current.includes(tag)) return null;
        return { tags: current.filter(t => t !== tag) };
      });
    },
    async bulkSetDueDate(date) {
      const value = date || null;
      await this._bulkApply([...this.checkedIds], () => ({ due_date: value }));
    },
    onQuickAdd(parentNode) {
      if (!this.expandedIds.has(parentNode.id)) {
        const next = new Set(this.expandedIds);
        next.add(parentNode.id);
        this.expandedIds = next;
      }
      this.quickAddParent = parentNode;
    },
    onRootDragStart() {
      document.body.classList.add('is-dragging-requirement');
    },
    onRootDragEnd(evt) {
      document.body.classList.remove('is-dragging-requirement');
      const fromParentId = evt.from.dataset.parentId;
      const toParentId = evt.to.dataset.parentId;
      const itemId = Number(evt.item?.dataset?.id);
      if (!Number.isFinite(itemId)) return;

      if (fromParentId !== toParentId) {
        this.onReparent({
          id: itemId,
          newParentId: toParentId ? Number(toParentId) : null,
          newIndex: evt.newIndex,
          visibleOrderIds: Array.from(evt.to.children).map((el) => Number(el.dataset.id)).filter(Number.isFinite),
        });
      } else {
        this.onReorder({ parentId: null, visibleOrderIds: this.filteredTree.map((node) => node.id) });
      }
    },
    siblingsOf(parentId) {
      return this.allFlat
        .filter((r) => (r.parent || null) === (parentId || null))
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0) || a.id - b.id);
    },
    async onReorder({ parentId, visibleOrderIds }) {
      const items = mergeSiblingOrder(this.siblingsOf(parentId), visibleOrderIds);
      const previous = items.map(({ id }) => ({ id, position: this.itemById(id)?.position }));
      for (const { id, position } of items) {
        this.$store.dispatch('supportItems/patchLocal', { id, fields: { position } });
      }
      try {
        await axios.post('/api/requirements/reorder/', { items });
        for (const { id } of items) this.$store.dispatch('supportItems/revert', { id, fields: {} });
      } catch {
        for (const { id, position } of previous) this.$store.dispatch('supportItems/revert', { id, fields: { position } });
        this.toast.error('Could not save the new order.');
      }
    },
    async onReparent({ id, newParentId, visibleOrderIds }) {
      const item = this.itemById(id);
      const parent = newParentId ? this.itemById(newParentId) : null;
      if (!item) return;
      if (newParentId && !parent) {
        this.treeVersion++;
        return;
      }
      if (parent && !canParent(parent.requirement_type_name, item.requirement_type_name)) {
        this.toast.error(`A ${item.requirement_type_name} can't live under a ${parent.requirement_type_name}.`);
        this.treeVersion++;
        return;
      }
      const previous = { parent: item.parent, position: item.position };
      const order = mergeSiblingOrder(
        [...this.siblingsOf(newParentId), item].filter((r, i, arr) => arr.findIndex((x) => x.id === r.id) === i),
        visibleOrderIds || [],
      );
      const position = order.find((o) => o.id === id)?.position ?? 0;
      try {
        await this.$store.dispatch('supportItems/update', { id, payload: { parent: newParentId || null, position } });
        await this.onReorder({ parentId: newParentId, visibleOrderIds: visibleOrderIds || [] });
      } catch {
        this.$store.dispatch('supportItems/revert', { id, fields: previous });
        this.treeVersion++;
        this.toast.error('Could not move that item.');
      }
    },
    async onQuickAddCreated(data) {
      try {
        const { data: created } = await axios.post('/api/requirements/', data);
        this.$store.dispatch('supportItems/addToScope', { scope: SCOPE, row: created });
      } catch {
        this.toast.error('Could not create that item.');
      }
    },
    onRequirementCreated(created) {
      this.showCreateModal = false;
      this.createModalParentId = null;
      if (created?.id) {
        this.$store.dispatch('supportItems/addToScope', { scope: SCOPE, row: created });
        this.$emit('select', created.id);
      }
    },
    onRequirementDeleted(id) {
      if (id) this.$store.dispatch('supportItems/remove', id);
      this.$emit('select', null);
    },
    openCreateWithParent(parentItem) {
      this.createModalParentId = parentItem.id;
      this.showCreateModal = true;
    },
    onActionsMenu({ event, node }) {
      const rect = event.currentTarget.getBoundingClientRect();
      this.contextMenu = { visible: true, x: rect.right, y: rect.bottom + 4, item: node };
    },
    onContextChangeParent(item) {
      this.$emit('select', item.id);
    },
    async onContextCopyLink(item) {
      const query = new URLSearchParams(this.buildQuery()).toString();
      const url = `${window.location.origin}/support/work/${item.id}${query ? `?${query}` : ''}`;
      try {
        await navigator.clipboard.writeText(url);
        this.toast.success('Link copied to clipboard.');
      } catch {
        this.toast.error('Failed to copy link.');
      }
    },
    async onContextDelete(item) {
      try {
        await axios.delete(`/api/requirements/${item.id}/`);
        this.toast.success('Deleted successfully.');
        this.$store.dispatch('supportItems/remove', item.id);
        if (this.selectedIdNumber === item.id) this.$emit('select', null);
      } catch {
        this.toast.error('Failed to delete. Please try again.');
      }
    },
    async onContextDeletePromote(item) {
      const children = this.allFlat.filter((r) => r.parent === item.id);
      const failed = [];
      for (const child of children) {
        try {
          await this.$store.dispatch('supportItems/update', { id: child.id, payload: { parent: item.parent || null } });
        } catch {
          failed.push(child.title);
        }
      }
      if (failed.length) {
        this.toast.error(
          `Could not move ${failed.length} ${failed.length === 1 ? 'child' : 'children'}. Delete aborted.`,
        );
        return;
      }
      try {
        await axios.delete(`/api/requirements/${item.id}/`);
        this.toast.success('Deleted. Children moved to parent.');
        this.$store.dispatch('supportItems/remove', item.id);
        if (this.selectedIdNumber === item.id) this.$emit('select', null);
      } catch {
        this.toast.error('Children were moved but delete failed. Please try again.');
      }
    },
    onCommentCount({ id, count }) {
      this.commentCounts = { ...this.commentCounts, [id]: count };
    },
    exportCsv() {
      const formatStatus = (s) => STATUS_LABELS[s] || (s || '');
      const rows = [['ID', 'Type', 'Title', 'Status', 'Priority', 'Tenant', 'Owner', 'Requester', 'Due Date']];
      const items = this.viewMode === 'table' ? this.tableData : this.tagFiltered;
      const cell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
      for (const item of items) {
        rows.push([
          item.id,
          item.requirement_type_name || '',
          item.title || '',
          formatStatus(item.status),
          priorityShortLabel(item.priority),
          item.tenant_name || '',
          item.owner_name || '',
          item.requester_name || '',
          item.due_date || '',
        ].map(cell));
      }
      const csv = rows.map((r) => r.join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `support-work-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
    // Keyboard navigation. Only for the list itself: inputs, buttons, selects
    // and anything inside the detail tray keep their own keys.
    onKeydown(e) {
      const target = e.target;
      const tag = target.tagName;
      if (['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON'].includes(tag)) return;
      if (target.isContentEditable) return;
      if (target.closest?.('.tray-panel')) return;
      if (this.showCreateModal || this.filterPopoverOpen || this.bulkDropdown) return;
      const ids = this.flatVisibleIds;

      if (e.key === 'ArrowDown' || e.key === 'j') {
        if (!ids.length) return;
        e.preventDefault();
        const currentIndex = ids.indexOf(this.keyboardFocusedId);
        const next = currentIndex < ids.length - 1 ? currentIndex + 1 : 0;
        this.keyboardFocusedId = ids[next];
        this.scrollFocusedIntoView();
      } else if (e.key === 'ArrowUp' || e.key === 'k') {
        if (!ids.length) return;
        e.preventDefault();
        const currentIndex = ids.indexOf(this.keyboardFocusedId);
        const prev = currentIndex > 0 ? currentIndex - 1 : ids.length - 1;
        this.keyboardFocusedId = ids[prev];
        this.scrollFocusedIntoView();
      } else if (e.key === 'Enter') {
        if (!this.keyboardFocusedId) return;
        e.preventDefault();
        const item = this.itemById(this.keyboardFocusedId);
        if (item) this.onSelect(item);
      } else if (e.key === 'Escape') {
        if (this.selectedIdNumber) {
          this.closeDetail();
        } else if (this.bulkMode) {
          this.bulkMode = false;
          this.checkedIds = new Set();
        } else {
          this.keyboardFocusedId = null;
        }
      } else if (e.key === 'ArrowRight' && this.viewMode === 'tree') {
        e.preventDefault();
        if (this.keyboardFocusedId && !this.expandedIds.has(this.keyboardFocusedId)) {
          this.onToggle(this.keyboardFocusedId);
        }
      } else if (e.key === 'ArrowLeft' && this.viewMode === 'tree') {
        e.preventDefault();
        if (this.keyboardFocusedId && this.expandedIds.has(this.keyboardFocusedId)) {
          this.onToggle(this.keyboardFocusedId);
        }
      } else if (e.key === 'x' && this.bulkMode && this.keyboardFocusedId) {
        this.onToggleCheck(this.keyboardFocusedId);
      }
    },
    scrollFocusedIntoView() {
      this.$nextTick(() => {
        const el = this.$el?.querySelector(`[data-id="${this.keyboardFocusedId}"]`);
        el?.scrollIntoView?.({ block: 'nearest' });
      });
    },
  },
};
</script>
